// Protected Application Header & Navigation Bars
import { getCurrentProfile, logoutUser } from '../services/authService';
import { navigateTo } from '../router';
import { getDadDisplayName } from '../types';

export type AppTab = 'feed' | 'crew' | 'badges' | 'profile';

export function renderAppNavigation(activeTab: AppTab, maydaysCount: number, pendingCrewRequestsCount: number = 0): string {
  const profile = getCurrentProfile();
  if (!profile) return '';

  return `
    <header class="bg-[#06070a]/95 border-b border-white/[0.08] sticky top-0 z-40 backdrop-blur-md sticky-header">
      <div class="laser-line-cyan absolute bottom-0 left-0 right-0 opacity-40"></div>
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="flex items-center justify-between h-16">
          
          <!-- Logo & Tagline -->
          <div class="flex items-center gap-3 cursor-pointer" id="nav-brand-logo">
            <div class="w-15 h-15 rounded-xl flex items-center justify-center p-0 hover:shadow-[0_0_18px_rgba(0,240,255,0.5)] transition-all">
                <img src="/logo.png" alt="Pops Culture Logo" class="w-full h-full object-contain">
              </div>
            <div>
              <div class="flex items-center gap-2">
                <span class="text-lg font-black tracking-wider uppercase"><span class="text-[#FF2A85]">Pops</span> <span class="text-[#00f0ff]">Culture</span></span>
              </div>
              <p class="text-[11px] text-slate-400 hidden sm:block">Where dad jokes are encouraged</p>
            </div>
          </div>

          <!-- Desktop Navigation Tabs -->
          <nav class="hidden md:flex items-center gap-2">
            <button data-tab="feed" class="app-tab-btn px-3.5 py-1.5 rounded-lg text-xs uppercase tracking-wider font-bold transition-all flex items-center gap-2 ${activeTab === 'feed' ? 'bg-transparent text-cyan-300 border border-cyan-400 shadow-[0_0_12px_rgba(0,240,255,0.25)]' : 'text-slate-400 hover:text-slate-200 border border-transparent hover:border-white/10'}">
              <i class="fa-solid fa-compass"></i> Missions
            </button>
            <button data-tab="crew" class="app-tab-btn px-3.5 py-1.5 rounded-lg text-xs uppercase tracking-wider font-bold transition-all flex items-center gap-2 ${activeTab === 'crew' ? 'bg-transparent text-cyan-300 border border-cyan-400 shadow-[0_0_12px_rgba(0,240,255,0.25)]' : 'text-slate-400 hover:text-slate-200 border border-transparent hover:border-white/10'}">
              <i class="fa-solid fa-users"></i> The Crew & Maydays
              ${pendingCrewRequestsCount > 0 ? `<span class="border border-emerald-400 text-emerald-400 text-[10px] px-1.5 py-0.2 rounded-full font-bold shadow-[0_0_8px_rgba(0,255,136,0.4)] animate-pulse" title="${pendingCrewRequestsCount} friend requests">${pendingCrewRequestsCount}</span>` : ''}
              ${maydaysCount > 0 ? `<span class="border border-pink-500 text-pink-400 text-[10px] px-1.5 py-0.2 rounded-full font-bold shadow-[0_0_8px_rgba(255,42,133,0.5)] animate-pulse">${maydaysCount}</span>` : ''}
            </button>

            <button data-tab="badges" class="app-tab-btn px-3.5 py-1.5 rounded-lg text-xs uppercase tracking-wider font-bold transition-all flex items-center gap-2 ${activeTab === 'badges' ? 'bg-transparent text-cyan-300 border border-cyan-400 shadow-[0_0_12px_rgba(0,240,255,0.25)]' : 'text-slate-400 hover:text-slate-200 border border-transparent hover:border-white/10'}">
              <i class="fa-solid fa-medal"></i> Honor Roll
            </button>
            <button data-tab="profile" class="app-tab-btn px-3.5 py-1.5 rounded-lg text-xs uppercase tracking-wider font-bold transition-all flex items-center gap-2 ${activeTab === 'profile' ? 'bg-transparent text-cyan-300 border border-cyan-400 shadow-[0_0_12px_rgba(0,240,255,0.25)]' : 'text-slate-400 hover:text-slate-200 border border-transparent hover:border-white/10'}">
              <i class="fa-solid fa-id-card"></i> Dad Resume
            </button>
          </nav>

          <!-- Right Quick Actions: Post Mission & Dad Profile Dropdown -->
          <div class="flex items-center gap-3">
            <button id="header-rally-crew-btn" class="bg-[#090c15] text-cyan-300 border border-cyan-400 hover:border-[#38f9d7] hover:text-white font-bold px-3.5 py-2 rounded-lg text-xs uppercase tracking-wider flex items-center gap-2 shadow-[0_0_14px_rgba(0,240,255,0.3)] active:scale-95 transition-all">
              <i class="fa-solid fa-plus text-xs"></i>
              <span class="hidden sm:inline">Rally Crew</span>
            </button>

            <!-- Dad Profile Dropdown Pill -->
            <div class="relative">
              <button id="user-menu-btn" class="flex items-center gap-2 p-1.5 pl-2.5 rounded-lg bg-[#0a0c13] border border-white/[0.1] hover:border-cyan-400/40 transition-all cursor-pointer">
                <div class="text-right hidden xl:block">
                  <div class="text-xs font-bold text-slate-200">${getDadDisplayName(profile)}</div>
                  <div class="text-[10px] text-pink-400 flex items-center justify-end gap-1 font-medium">
                    <i class="fa-solid fa-shield text-[9px]"></i> ${profile.badgesEarned.length} Badges
                  </div>
                </div>
                <div class="w-8 h-8 rounded-lg bg-transparent border border-pink-500/60 text-pink-400 flex items-center justify-center font-bold text-xs shadow-[0_0_8px_rgba(255,42,133,0.3)]">
                  ${profile.avatarInitials}
                </div>
                <i class="fa-solid fa-chevron-down text-[10px] text-slate-400 ml-0.5"></i>
              </button>

              <!-- Dropdown Menu -->
              <div id="user-dropdown-menu" class="hidden absolute right-0 mt-2 w-52 bg-[#0c0e17] border border-white/[0.15] rounded-xl shadow-[0_10px_30px_rgba(0,0,0,0.8)] py-2 z-50 text-xs animate-fade-in">
                <div class="px-4 py-2 border-b border-white/[0.08]">
                  <div class="font-bold text-slate-200 truncate">${getDadDisplayName(profile)}</div>
                  ${profile.displayName ? `<div class="text-[10px] text-slate-400 truncate">Google: ${profile.name}</div>` : ''}
                  <div class="text-[11px] text-slate-400 truncate">${profile.email}</div>
                </div>
                <button data-tab="profile" class="app-tab-btn w-full text-left px-4 py-2 text-slate-300 hover:bg-white/[0.05] hover:text-cyan-400 flex items-center gap-2">
                  <i class="fa-solid fa-id-card text-cyan-400 w-4"></i>
                  <span>My Dad Resume</span>
                </button>
                <button id="dropdown-landing-btn" class="w-full text-left px-4 py-2 text-slate-300 hover:bg-white/[0.05] hover:text-cyan-400 flex items-center gap-2">
                  <i class="fa-solid fa-house text-slate-400 w-4"></i>
                  <span>Public Landing Page</span>
                </button>
                <div class="laser-line my-1"></div>
                <button id="dropdown-logout-btn" class="w-full text-left px-4 py-2 text-pink-400 hover:bg-pink-950/20 flex items-center gap-2">
                  <i class="fa-solid fa-right-from-bracket w-4"></i>
                  <span>Sign Out</span>
                </button>
              </div>
            </div>

          </div>

        </div>
      </div>
    </header>

    <!-- Mobile Fixed Bottom Navigation Bar -->
    <nav class="md:hidden fixed bottom-0 left-0 right-0 bg-[#06070a]/95 border-t border-white/[0.08] backdrop-blur-md px-3 py-2 z-40 flex items-center justify-around shadow-2xl relative">
      <div class="laser-line-cyan absolute top-0 left-0 right-0 opacity-40"></div>
      <button data-tab="feed" class="app-tab-btn flex flex-col items-center gap-1 ${activeTab === 'feed' ? 'text-cyan-400 font-bold' : 'text-slate-400'} text-[10px]">
        <i class="fa-solid fa-compass text-base"></i>
        <span>Missions</span>
      </button>
      <button data-tab="crew" class="app-tab-btn flex flex-col items-center gap-1 ${activeTab === 'crew' ? 'text-cyan-400 font-bold' : 'text-slate-400'} text-[10px] relative">
        <i class="fa-solid fa-users text-base"></i>
        <span>Crew</span>
        ${pendingCrewRequestsCount > 0 ? `<span class="absolute -top-1 -right-1 border border-emerald-400 text-emerald-400 bg-black text-[9px] w-4 h-4 flex items-center justify-center rounded-full font-bold shadow-[0_0_8px_rgba(0,255,136,0.5)]">${pendingCrewRequestsCount}</span>` : maydaysCount > 0 ? `<span class="absolute -top-1 -right-1 border border-pink-500 text-pink-400 bg-black text-[9px] w-4 h-4 flex items-center justify-center rounded-full font-bold shadow-[0_0_8px_rgba(255,42,133,0.5)]">${maydaysCount}</span>` : ''}
      </button>
      <button id="mobile-rally-btn" class="flex flex-col items-center -mt-6 bg-[#090c15] text-cyan-400 border border-cyan-400 w-12 h-12 rounded-full items-center justify-center shadow-[0_0_15px_rgba(0,240,255,0.4)] active:scale-95 transition-transform">
        <i class="fa-solid fa-plus text-base font-black"></i>
      </button>
      <button data-tab="badges" class="app-tab-btn flex flex-col items-center gap-1 ${activeTab === 'badges' ? 'text-cyan-400 font-bold' : 'text-slate-400'} text-[10px]">
        <i class="fa-solid fa-medal text-base"></i>
        <span>Badges</span>
      </button>
      <button data-tab="profile" class="app-tab-btn flex flex-col items-center gap-1 ${activeTab === 'profile' ? 'text-cyan-400 font-bold' : 'text-slate-400'} text-[10px]">
        <i class="fa-solid fa-id-card text-base"></i>
        <span>Resume</span>
      </button>
    </nav>
  `;
}

export function bindAppNavigationEvents(onTabSwitch: (tab: AppTab) => void, onRallyCrew: () => void): void {
  // Tab buttons
  document.querySelectorAll('.app-tab-btn').forEach(btn => {
    btn.addEventListener('click', () => {
      const tab = (btn as HTMLElement).dataset.tab as AppTab;
      if (tab) {
        onTabSwitch(tab);
      }
      // Close user dropdown if open
      document.getElementById('user-dropdown-menu')?.classList.add('hidden');
    });
  });

  // Rally Crew buttons (header & mobile)
  document.getElementById('header-rally-crew-btn')?.addEventListener('click', () => {
    onRallyCrew();
  });
  document.getElementById('mobile-rally-btn')?.addEventListener('click', () => {
    onRallyCrew();
  });

  // Brand logo click
  document.getElementById('nav-brand-logo')?.addEventListener('click', () => {
    onTabSwitch('feed');
  });

  // User menu toggle
  const userBtn = document.getElementById('user-menu-btn');
  const dropdown = document.getElementById('user-dropdown-menu');
  userBtn?.addEventListener('click', (e) => {
    e.stopPropagation();
    dropdown?.classList.toggle('hidden');
  });

  document.addEventListener('click', () => {
    dropdown?.classList.add('hidden');
  });

  // Public Landing Link
  document.getElementById('dropdown-landing-btn')?.addEventListener('click', () => {
    navigateTo('/');
  });

  // Sign out
  document.getElementById('dropdown-logout-btn')?.addEventListener('click', async () => {
    await logoutUser();
    navigateTo('/');
  });
}
